/**
 * Service Clients Configuration UI
 * View and override service identity KV configs.
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';

interface ServiceClientConfig {
  _version?: number;
  serviceId: string;
  environment: 'dev' | 'staging' | 'prod';
  status: 'active' | 'disabled';
  cognitoClientId?: string;
  cognitoClientSecret?: string;
  allowedUsernames: string[];
  allowedCallerUserIds?: string[];
  allowedScopes: string[];
  allowedDelegationAudiences: string[];
  allowedDelegationScopes: string[];
  delegationTtlMinSeconds: number;
  delegationTtlMaxSeconds: number;
}

interface ServiceClientEntry {
  config: ServiceClientConfig;
  source: 'default' | 'kv';
  healed: boolean;
  hasOverride: boolean;
}

interface ServiceClientsResponse {
  services: Record<string, ServiceClientEntry>;
  index: string[];
}

export class ServiceClientsUI extends BaseComponent {
  private apiClient: APIClient;
  private services: Record<string, ServiceClientEntry> = {};
  private editingServiceId: string | null = null;

  constructor(container: HTMLElement, apiClient: APIClient) {
    super(container);
    this.apiClient = apiClient;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="routing-config-ui">
        <div class="routing-header">
          <h2>🛡️ Service Clients</h2>
          <p class="subtitle">Manage service identity config (default + KV override)</p>
        </div>

        <div class="routing-toolbar">
          <div class="toolbar-left">
            <button id="add-service-btn" class="btn btn-primary">➕ Add Service</button>
          </div>
          <div class="toolbar-right">
            <button id="refresh-services-btn" class="btn btn-secondary">🔄 Refresh</button>
          </div>
        </div>

        <div id="service-clients-container" class="tasks-container">
          <div class="loading">Loading service clients...</div>
        </div>

        <div id="service-client-modal" class="modal" style="display: none;">
          <div class="modal-content">
            <div class="modal-header">
              <h3 id="service-client-modal-title">Edit Service Client</h3>
              <button id="service-client-modal-close" class="close-btn">&times;</button>
            </div>
            <div id="service-client-modal-body" class="modal-body"></div>
          </div>
        </div>
      </div>
    `;

    document.getElementById('refresh-services-btn')?.addEventListener('click', () => this.loadServices());
    document.getElementById('add-service-btn')?.addEventListener('click', () => this.addService());
    document.getElementById('service-client-modal-close')?.addEventListener('click', () => this.hideModal());

    await this.loadServices();
  }

  private async loadServices(): Promise<void> {
    const container = document.getElementById('service-clients-container');
    if (!container) return;

    container.innerHTML = '<div class="loading">⏳ Loading...</div>';
    try {
      const response = await this.apiClient.get<ServiceClientsResponse>('/v1/admin/config/service-clients');
      this.services = response.data.services || {};
      this.renderServices();
    } catch (error) {
      container.innerHTML = `
        <div class="error">
          <p>❌ Failed to load service clients</p>
          <p class="error-detail">${error instanceof Error ? error.message : 'Unknown'}</p>
        </div>
      `;
    }
  }

  private renderServices(): void {
    const container = document.getElementById('service-clients-container');
    if (!container) return;

    const entries = Object.entries(this.services).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) {
      container.innerHTML = '<div class="no-results">No service clients found</div>';
      return;
    }

    container.innerHTML = `
      <div class="tasks-grid">
        ${entries.map(([serviceId, entry]) => this.renderServiceCard(serviceId, entry)).join('')}
      </div>
    `;

    entries.forEach(([serviceId]) => {
      document.getElementById(`edit-service-${serviceId}`)?.addEventListener('click', () => this.showEditModal(serviceId));
      document.getElementById(`reset-service-${serviceId}`)?.addEventListener('click', () => this.resetService(serviceId));
    });
  }

  private renderServiceCard(serviceId: string, entry: ServiceClientEntry): string {
    const cfg = entry.config;
    const badge = entry.hasOverride ? 'KV Override' : 'Default';
    const badgeColor = entry.hasOverride ? '#3b82f6' : '#64748b';
    const statusColor = cfg.status === 'active' ? '#10b981' : '#ef4444';

    return `
      <div class="task-card">
        <div class="task-header">
          <div>
            <h4>${this.escapeHtml(serviceId)}</h4>
            <span class="quality-badge" style="background:${badgeColor};color:white;">${badge}</span>
            ${entry.healed ? '<span class="quality-badge">auto-healed</span>' : ''}
          </div>
          <div class="model-actions">
            <button id="edit-service-${this.escapeHtml(serviceId)}" class="icon-btn" title="Edit">✏️</button>
            <button id="reset-service-${this.escapeHtml(serviceId)}" class="icon-btn" title="Reset KV override">🔄</button>
          </div>
        </div>
        <div class="task-models">
          <p><strong>Status:</strong> <span style="color:${statusColor}">${this.escapeHtml(cfg.status)}</span></p>
          <p><strong>Env:</strong> <code>${this.escapeHtml(cfg.environment)}</code></p>
          <p><strong>Allowed Users:</strong> ${cfg.allowedUsernames.length}</p>
          <p><strong>Delegation Audiences:</strong> ${cfg.allowedDelegationAudiences.length}</p>
          <p><strong>Delegation Scopes:</strong> ${cfg.allowedDelegationScopes.length}</p>
          <p><strong>TTL:</strong> ${cfg.delegationTtlMinSeconds}s - ${cfg.delegationTtlMaxSeconds}s</p>
        </div>
      </div>
    `;
  }

  private showEditModal(serviceId: string): void {
    const entry = this.services[serviceId];
    if (!entry) return;

    this.editingServiceId = serviceId;
    const modal = document.getElementById('service-client-modal');
    const title = document.getElementById('service-client-modal-title');
    const body = document.getElementById('service-client-modal-body');
    if (!modal || !title || !body) return;

    title.textContent = `Edit Service Client: ${serviceId}`;
    body.innerHTML = `
      <div class="task-edit-form">
        <div class="form-group">
          <label>Service ID</label>
          <input id="service-id-input" type="text" value="${this.escapeHtml(serviceId)}" readonly />
        </div>
        <div class="form-group">
          <label>JSON Config</label>
          <textarea id="service-config-json" rows="24" style="width:100%;font-family:ui-monospace, SFMono-Regular, Menlo, monospace;">${this.escapeHtml(JSON.stringify(entry.config, null, 2))}</textarea>
          <small>Must match service identity schema. This writes KV override for this serviceId.</small>
        </div>
        <div class="form-actions">
          <button id="cancel-service-client-btn" class="btn btn-secondary">Cancel</button>
          <button id="save-service-client-btn" class="btn btn-primary">Save</button>
        </div>
      </div>
    `;

    document.getElementById('cancel-service-client-btn')?.addEventListener('click', () => this.hideModal());
    document.getElementById('save-service-client-btn')?.addEventListener('click', () => this.saveService());

    modal.style.display = 'flex';
  }

  private async saveService(): Promise<void> {
    if (!this.editingServiceId) return;
    const textarea = document.getElementById('service-config-json') as HTMLTextAreaElement | null;
    if (!textarea) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(textarea.value);
    } catch (error) {
      alert(`❌ Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return;
    }

    try {
      await this.apiClient.put('/v1/admin/config/service-clients', {
        serviceId: this.editingServiceId,
        config: parsed,
      });
      alert('✅ Service client override saved');
      this.hideModal();
      await this.loadServices();
    } catch (error) {
      alert(`❌ Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async resetService(serviceId: string): Promise<void> {
    if (!confirm(`Reset KV override for "${serviceId}"?\n\nThis removes routing:service-clients:${serviceId}.`)) {
      return;
    }
    try {
      await this.apiClient.delete(`/v1/admin/config/service-clients/${encodeURIComponent(serviceId)}`);
      alert('✅ Service client override removed');
      await this.loadServices();
    } catch (error) {
      alert(`❌ Reset failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private addService(): void {
    const input = prompt('Enter new serviceId (e.g. consenger-livekit):', '');
    if (!input) return;
    const serviceId = input.trim();
    if (!serviceId) return;

    const template: ServiceClientConfig = {
      _version: 1,
      serviceId,
      environment: 'prod',
      status: 'active',
      allowedUsernames: [],
      allowedCallerUserIds: [],
      allowedScopes: ['service:delegate'],
      allowedDelegationAudiences: ['api.consenger.app'],
      allowedDelegationScopes: ['ai:chat'],
      delegationTtlMinSeconds: 60,
      delegationTtlMaxSeconds: 300,
    };

    this.services[serviceId] = {
      config: template,
      source: 'default',
      healed: false,
      hasOverride: false,
    };
    this.showEditModal(serviceId);
  }

  private hideModal(): void {
    const modal = document.getElementById('service-client-modal');
    if (modal) modal.style.display = 'none';
    this.editingServiceId = null;
  }
}

